/**
 * ============================================================================
 * Notification 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Notification 模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createNotificationApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 导入工厂函数
 * import { createNotificationApi } from '@bayes-mes/shared/api/notification/factory'
 * import { requestClient } from '#/api/request'
 * 
 * // 2. 创建 API 实例
 * export const notificationApi = createNotificationApi(requestClient)
 * 
 * // 3. 使用 API
 * const result = await notificationApi.getList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器
 * const uniAdapter = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createNotificationApi } from '@bayes-mes/shared/api/notification/factory'
 * export const notificationApi = createNotificationApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **路径引用**：所有路径从 API_ROUTES 引用，禁止硬编码
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/notification/factory
 * @description Notification 模块 API 工厂函数
 * 
 * @source 对应后端文件:
 * - backend/app/Modules/Notification/Controllers/NotificationController.php
 * - backend/app/Modules/Notification/Controllers/NotificationAdminController.php
 * - backend/app/Modules/Notification/Routes/api.php
 * 
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-21
 * @last-updated 2025-10-22
 * 
 * @change-log
 * - 2025-10-22: 重构为规范版本，使用 API_ROUTES 引用，移除硬编码路径
 * - 2025-10-21: 初始创建
 * 
 * @notes
 * - API 路径前缀: /api/v1/notifications (用户端), /api/v1/admin/notifications (管理端)
 * - 使用工厂模式实现平台无关的 API 调用
 * - 支持完整的 TypeScript 类型推断
 * - IRequestClient 接口定义在 api/core/client.ts
 * ============================================================================
 */

// ============================================================================
// 类型导入
// ============================================================================

import type {
  // 响应类型
  Notification,
  NotificationTemplate,
  NotificationSetting,
  NotificationListResponse,
  UserNotificationSettingsResponse,
  
  // 请求类型
  SendNotificationForm,
  CreateNotificationTemplateForm,
  UpdateNotificationTemplateForm,
  UpdateNotificationSettingForm,
  MarkNotificationReadForm,
  UpdateMasterSwitchForm,
  BatchUpdateSettingsForm,
  
  // 查询参数
  NotificationQueryParams,
  NotificationTemplateQueryParams,
  NotificationChannelQueryParams,

  // 监控类型
  NotificationMonitorDashboard,
  SendRecordDetail,
  SendRecordsResponse,
  FailuresResponse,
  BatchRetryResponse,
  RetryResponse,
  SendRecordsQueryParams,
  FailuresQueryParams,
  BatchRetryParams,
} from '../../types/notification'

import type { PaginationResult } from '../../types/api'

// 导入统一的请求客户端接口
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// 导入 API 路由定义
// ⚠️ 重要：所有路径都从 API_ROUTES 引用，禁止硬编码！
import { API_ROUTES } from '../routes'

// ============================================================================
// 响应类型定义
// ============================================================================

/** 标记已读响应 */
interface MarkReadResponse {
  count: number
  message: string
}

/** 未读数量响应 */
interface UnreadCountResponse {
  count: number
  /** 各消息类型的未读数量（键为 NotificationType 值；无未读的类型不出现在映射中） */
  by_type?: Record<string, number>
}

/** 发送消息响应 */
interface SendNotificationResponse {
  notificationId: number
  receiverCount: number
  blockedUsers: number[]
  message: string
  warnings: string[]
}

/** 广播消息响应 */
interface BroadcastNotificationResponse {
  notificationId: number
  receiverCount: number
  message: string
}

/** 模板测试响应 */
interface TemplateTestResponse {
  title: string
  content: string
  emailSubject: string
  emailContent: string
}

/** 事件统计响应 */
interface EventStatisticsResponse {
  total: number
  byEventType: Record<string, number>
  byStatus: Record<string, number>
}

/** 注册推送设备表单 */
interface RegisterPushDeviceForm {
  cid: string
  device_id: string
  platform: 'android' | 'ios' | 'harmonyos'
  device_name?: string
  device_model?: string
  os_version?: string
  app_version?: string
}

/** 注册设备响应 */
interface RegisterDeviceResponse {
  message: string
  device_id: string
  cid: string
  platform: string
}

/** 推送设备信息 */
interface PushDevice {
  id: number
  device_id: string
  platform: string
  device_name: string | null
  device_model: string | null
  is_enabled: boolean
  last_active_at: string | null
}

// ============================================================================
// 系统级通知配置类型（管理员使用）
// ============================================================================

/** 渠道配置 */
interface ChannelConfig {
  channel: string
  label: string
  enabled: boolean
}

/** 限流配置 */
interface RateLimitConfig {
  enabled: boolean
  max_per_minute?: number
  max_per_hour?: number
}

/** 免打扰时段配置 */
interface QuietHoursConfig {
  enabled: boolean
  start_time?: string
  end_time?: string
  exclude_levels?: string[]
}

/** 系统级通知配置 */
interface SystemNotificationConfig {
  master_enabled: boolean
  channels: ChannelConfig[]
  rate_limit: RateLimitConfig
  quiet_hours: QuietHoursConfig
}

/** 接收人配置 */
interface RecipientsConfig {
  user_ids?: number[]
  role_ids?: number[]
  department_ids?: number[]
  use_business_owner?: boolean
  owner_field?: string
}

/** 系统级通知规则 */
interface SystemRule {
  id: number
  module: string
  eventType: string
  eventName: string
  description: string | null
  isEnabled: boolean
  level: 'info' | 'success' | 'warning' | 'error'
  channels: string[]
  recipients: RecipientsConfig
  sortOrder: number
}

/** 模块及其规则 */
interface ModuleWithRules {
  module: string
  name: string
  rules: SystemRule[]
}

/** 创建系统规则表单 */
interface CreateSystemRuleForm {
  module: string
  event_type: string
  event_name: string
  description?: string
  is_enabled?: boolean
  level?: 'info' | 'success' | 'warning' | 'error'
  channels?: string[]
  recipients?: RecipientsConfig
  sort_order?: number
}

/** 更新系统规则表单 */
interface UpdateSystemRuleForm {
  event_name?: string
  description?: string
  is_enabled?: boolean
  level?: 'info' | 'success' | 'warning' | 'error'
  channels?: string[]
  recipients?: RecipientsConfig
  sort_order?: number
}

// ============================================================================
// 工厂函数
// ============================================================================

/**
 * 创建 Notification 模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有 Notification 相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有 Notification 相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createNotificationApi(client: IRequestClient) {
  return {
    // ===== 用户端 API - 消息管理 =====
    
    /**
     * 获取当前用户的消息列表（分页）
     * 
     * 使用场景：
     * - 在消息列表页面显示所有消息
     * - 支持按已读/未读、类型、级别筛选
     * - 支持关键词搜索和分页
     * 
     * @param params - 查询参数对象（可选）
     *   - page?: number - 页码，从 1 开始
     *   - per_page?: number - 每页数量，默认 20
     *   - is_read?: boolean - 筛选已读/未读
     *   - type?: NotificationType - 消息类型
     *   - level?: NotificationLevel - 消息级别
     *   - tag?: string - 标签筛选
     *   - keyword?: string - 关键词搜索
     * 
     * @returns Promise<NotificationListResponse> 分页的消息列表
     *   返回数据结构：
     *   {
     *     items: Notification[],
     *     pagination: {
     *       total: number,
     *       perPage: number,
     *       currentPage: number,
     *       lastPage: number
     *     }
     *   }
     * 
     * 代码示例：
     * ```typescript
     * // 获取未读消息
     * const unread = await notificationApi.getList({ is_read: false })
     * 
     * // 分页查询
     * const page2 = await notificationApi.getList({ page: 2, per_page: 20 })
     * ```
     */
    getList: (params?: NotificationQueryParams) => 
      client.get<NotificationListResponse>(API_ROUTES.notification.list, { params }),

    /**
     * 获取消息详情
     * 
     * 使用场景：
     * - 点击消息列表项查看详情
     * - 自动标记该消息为已读
     * 
     * @param id - 消息ID
     * 
     * @returns Promise<Notification> 单个消息对象
     */
    getDetail: (id: number) => 
      client.get<Notification>(API_ROUTES.notification.detail(id)),

    /**
     * 标记消息为已读
     * 
     * 使用场景：
     * - 批量标记选中的消息为已读
     * - 单个消息标记已读
     * 
     * @param data - 包含消息ID列表的表单
     *   - notification_ids: number[] - 要标记的消息ID数组
     * 
     * @returns Promise<MarkReadResponse> 标记结果
     *   { count: number, message: string }
     * 
     * 代码示例：
     * ```typescript
     * // 标记单个消息
     * await notificationApi.markRead({ notification_ids: [123] })
     * 
     * // 批量标记
     * await notificationApi.markRead({ notification_ids: [1, 2, 3] })
     * ```
     */
    markRead: (data: MarkNotificationReadForm) => 
      client.post<MarkReadResponse>(API_ROUTES.notification.markRead, data),

    /**
     * 标记所有消息为已读
     * 
     * 使用场景：
     * - 点击"全部标记为已读"按钮
     * - 清空所有未读消息
     * 
     * @returns Promise<MarkReadResponse> 标记结果
     */
    markAllRead: () => 
      client.post<MarkReadResponse>(API_ROUTES.notification.markAllRead),

    /**
     * 删除消息
     * 
     * 使用场景：
     * - 删除单个消息
     * - 清理不需要的消息
     * 
     * @param id - 消息ID
     * 
     * @returns Promise<void> 删除成功无返回数据
     */
    delete: (id: number) => 
      client.delete<void>(API_ROUTES.notification.delete(id)),

    /**
     * 获取未读消息数量
     * 
     * 使用场景：
     * - 在导航栏显示未读消息徽章
     * - 定时轮询更新未读数量
     * 
     * @returns Promise<UnreadCountResponse> 未读数量
     *   { count: number }
     * 
     * 代码示例：
     * ```typescript
     * const { count } = await notificationApi.getUnreadCount()
     * console.log(`您有 ${count} 条未读消息`)
     * ```
     */
    getUnreadCount: () => 
      client.get<UnreadCountResponse>(API_ROUTES.notification.unreadCount),

    /**
     * 获取消息统计信息
     * 
     * 使用场景：
     * - 在仪表盘显示消息统计
     * - 分析消息分布情况
     * 
     * @returns Promise<any> 统计数据
     */
    getStatistics: () => 
      client.get(API_ROUTES.notification.statistics),

    /**
     * 获取所有标签列表
     * 
     * 使用场景：
     * - 在筛选条件中显示可选标签
     * - 消息分类展示
     * 
     * @returns Promise<string[]> 标签数组
     */
    getTags: () => 
      client.get<string[]>(API_ROUTES.notification.tags),

    // ===== 用户端 API - 设置管理 =====

    /**
     * 获取当前用户的消息设置
     *
     * 使用场景：
     * - 在消息设置页面显示当前配置
     * - 查看各事件类型的通知渠道
     *
     * @returns Promise<UserNotificationSettingsResponse> 对象映射：master_enabled 总开关 + 各事件类型键到配置
     */
    getSettings: () =>
      client.get<UserNotificationSettingsResponse>(API_ROUTES.notificationSetting.list),

    /**
     * 更新用户消息总开关
     * 
     * 使用场景：
     * - 用户开启/关闭所有消息通知
     * - 总开关关闭后，所有消息都不推送
     * 
     * @param data - 总开关设置
     *   - enabled: boolean - 是否启用消息通知
     * 
     * @returns Promise<void>
     */
    updateMasterSwitch: (data: UpdateMasterSwitchForm) => 
      client.put(API_ROUTES.notificationSetting.master, data),

    /**
     * 更新单个事件的消息设置
     * 
     * 使用场景：
     * - 用户为特定事件类型配置通知渠道
     * - 开启/关闭某个事件的通知
     * 
     * @param eventType - 事件类型（如 'order.created'）
     * @param data - 事件设置数据
     *   - event_type: string - 事件类型
     *   - is_enabled: boolean - 是否启用
     *   - channels: NotificationChannel[] - 通知渠道数组
     * 
     * @returns Promise<NotificationSetting> 更新后的设置
     * 
     * 代码示例：
     * ```typescript
     * // 为订单创建事件配置通知
     * await notificationApi.updateEventSetting('order.created', {
     *   event_type: 'order.created',
     *   is_enabled: true,
     *   channels: ['web', 'wecom', 'app']
     * })
     * ```
     */
    updateEventSetting: (eventType: string, data: UpdateNotificationSettingForm) => 
      client.put<NotificationSetting>(API_ROUTES.notificationSetting.event(eventType), data),

    /**
     * 批量更新消息设置
     * 
     * 使用场景：
     * - 一次性更新多个事件的设置
     * - 批量导入设置配置
     * 
     * @param data - 批量设置数据
     *   - settings: UpdateNotificationSettingForm[] - 设置数组
     * 
     * @returns Promise<void>
     */
    batchUpdateSettings: (data: BatchUpdateSettingsForm) => 
      client.put(API_ROUTES.notificationSetting.batch, data),

    /**
     * 恢复默认消息设置
     * 
     * 使用场景：
     * - 用户重置所有自定义设置
     * - 恢复到系统默认配置
     * 
     * @returns Promise<void>
     */
    resetSettings: () => 
      client.post(API_ROUTES.notificationSetting.reset),

    /**
     * 获取可用消息渠道列表
     * 
     * 使用场景：
     * - 在设置页面显示所有可选渠道
     * - 动态展示系统支持的通知方式
     * 
     * @returns Promise<string[]> 渠道数组（如 ['web', 'wecom', 'app', 'sms', 'email']）
     */
    getChannels: () => 
      client.get<string[]>(API_ROUTES.notificationSetting.list),

    // ===== 管理端 API - 消息发送 =====

    /**
     * 获取所有用户列表（用于消息发送表单）
     * 
     * 使用场景：
     * - 在消息发送表单中填充用户选择下拉列表
     * - 支持多选用户
     * 
     * @returns Promise<Array<{ id: number; name: string; username: string; email?: string }>> 用户列表
     */
    getUsers: () => 
      client.get<Array<{ id: number; name: string; username: string; email?: string }>>(API_ROUTES.notificationAdmin.users),

    /**
     * 获取所有部门列表（用于消息发送表单）
     * 
     * 使用场景：
     * - 在消息发送表单中填充部门选择下拉列表
     * - 支持多选部门
     * 
     * @returns Promise<Array<{ id: number; name: string; parent_id?: number }>> 部门列表
     */
    getDepartments: () => 
      client.get<Array<{ id: number; name: string; parent_id?: number }>>(API_ROUTES.notificationAdmin.departments),

    /**
     * 发送消息（管理员）
     * 
     * 使用场景：
     * - 管理员向指定用户或部门发送消息
     * - 支持多渠道推送
     * - 支持业务对象关联
     * 
     * @param data - 发送消息表单数据
     *   - title: string - 消息标题
     *   - content: string - 消息内容
     *   - type: NotificationType - 消息类型
     *   - level?: NotificationLevel - 消息级别
     *   - priority?: number - 优先级（1-5）
     *   - channels?: NotificationChannel[] - 推送渠道
     *   - target_type: TargetType - 目标类型（'user', 'department', 'all'）
     *   - target_ids?: number[] - 目标ID数组
     *   - tags?: string[] - 标签
     *   - related_type?: string - 关联对象类型
     *   - related_id?: number - 关联对象ID
     * 
     * @returns Promise<SendNotificationResponse> 发送结果
     *   {
     *     notificationId: number,
     *     receiverCount: number,
     *     blockedUsers: number[],
     *     message: string,
     *     warnings: string[]
     *   }
     * 
     * 代码示例：
     * ```typescript
     * // 向指定用户发送系统通知
     * const result = await notificationApi.send({
     *   title: '系统维护通知',
     *   content: '系统将于今晚22:00进行维护',
     *   type: 'system',
     *   level: 'warning',
     *   priority: 3,
     *   channels: ['web', 'app', 'wecom'],
     *   target_type: 'user',
     *   target_ids: [1, 2, 3]
     * })
     * console.log(`已发送给 ${result.receiverCount} 个用户`)
     * ```
     */
    send: (data: SendNotificationForm) => 
      client.post<SendNotificationResponse>(API_ROUTES.notificationAdmin.send, data),

    /**
     * 广播消息（全员）
     * 
     * 使用场景：
     * - 向所有用户发送系统公告
     * - 紧急通知全员
     * 
     * @param data - 广播消息表单数据（同 send，但自动设置 target_type='all'）
     * 
     * @returns Promise<BroadcastNotificationResponse> 广播结果
     *   {
     *     notificationId: number,
     *     receiverCount: number,
     *     message: string
     *   }
     */
    broadcast: (data: SendNotificationForm) => 
      client.post<BroadcastNotificationResponse>(API_ROUTES.notificationAdmin.broadcast, data),

    // ===== 管理端 API - 全局设置 =====

    /**
     * 获取全局消息设置
     * 
     * 使用场景：
     * - 查看系统级别的消息配置
     * - 管理员配置默认设置
     * 
     * @returns Promise<any> 全局设置对象
     */
    getGlobalSettings: () => 
      client.get(API_ROUTES.notificationAdmin.notificationGlobalSettings),

    /**
     * 更新全局消息设置
     * 
     * 使用场景：
     * - 管理员修改系统默认配置
     * - 批量调整所有用户的默认设置
     * 
     * @param data - 全局设置数据
     * 
     * @returns Promise<void>
     */
    updateGlobalSettings: (data: Record<string, any>) => 
      client.put(API_ROUTES.notificationAdmin.notificationGlobalSettings, data),

    /**
     * 同步Redis缓存
     * 
     * 使用场景：
     * - 更新设置后同步到缓存
     * - 手动刷新缓存数据
     * 
     * @returns Promise<void>
     */
    syncCache: () => 
      client.post(API_ROUTES.notificationAdmin.sync),

    // ===== 管理端 API - 模板管理 =====

    /**
     * 获取消息模板列表（分页）
     * 
     * 使用场景：
     * - 在模板管理页面显示所有模板
     * - 支持按事件类型、启用状态筛选
     * 
     * @param params - 查询参数
     *   - event_type?: string - 事件类型筛选
     *   - is_enabled?: boolean - 启用状态筛选
     *   - keyword?: string - 关键词搜索
     *   - page?: number - 页码
     *   - per_page?: number - 每页数量
     * 
     * @returns Promise<PaginationResult<NotificationTemplate>> 分页的模板列表
     */
    getTemplateList: (params?: NotificationTemplateQueryParams) => 
      client.get<PaginationResult<NotificationTemplate>>(API_ROUTES.notificationAdmin.notificationTemplates, { params }),

    /**
     * 获取消息模板详情
     * 
     * 使用场景：
     * - 编辑模板前查看当前内容
     * - 预览模板配置
     * 
     * @param id - 模板ID
     * 
     * @returns Promise<NotificationTemplate> 模板详情
     */
    getTemplateDetail: (id: number) => 
      client.get<NotificationTemplate>(API_ROUTES.notificationAdmin.detail(id)),

    /**
     * 创建消息模板
     * 
     * 使用场景：
     * - 为新的事件类型创建通知模板
     * - 配置标题、内容、短信、邮件模板
     * 
     * @param data - 模板表单数据
     *   - event_type: string - 事件类型
     *   - name: string - 模板名称
     *   - description?: string - 描述
     *   - title_template: string - 标题模板
     *   - content_template: string - 内容模板
     *   - sms_template_code?: string - 短信模板代码
     *   - sms_params?: object - 短信参数
     *   - email_subject_template?: string - 邮件主题模板
     *   - email_content_template?: string - 邮件内容模板
     *   - is_enabled?: boolean - 是否启用
     * 
     * @returns Promise<NotificationTemplate> 创建的模板
     * 
     * 代码示例：
     * ```typescript
     * const template = await notificationApi.createTemplate({
     *   event_type: 'order.created',
     *   name: '订单创建通知',
     *   title_template: '您有新订单：{order_no}',
     *   content_template: '订单号：{order_no}，金额：{amount}',
     *   is_enabled: true
     * })
     * ```
     */
    createTemplate: (data: CreateNotificationTemplateForm) => 
      client.post<NotificationTemplate>(API_ROUTES.notificationAdmin.notificationTemplates, data),

    /**
     * 更新消息模板
     * 
     * 使用场景：
     * - 修改模板内容
     * - 更新模板配置
     * 
     * @param id - 模板ID
     * @param data - 更新数据
     * 
     * @returns Promise<NotificationTemplate> 更新后的模板
     */
    updateTemplate: (id: number, data: UpdateNotificationTemplateForm) => 
      client.put<NotificationTemplate>(API_ROUTES.notificationAdmin.adminNotificationTemplates(id), data),

    /**
     * 删除消息模板
     * 
     * 使用场景：
     * - 删除不再使用的模板
     * - 清理过时配置
     * 
     * @param id - 模板ID
     * 
     * @returns Promise<void>
     */
    deleteTemplate: (id: number) => 
      client.delete(API_ROUTES.notificationAdmin.delete(id)),

    /**
     * 测试消息模板
     * 
     * 使用场景：
     * - 预览模板渲染效果
     * - 验证变量替换是否正确
     * 
     * @param id - 模板ID
     * @param testData - 测试数据（用于变量替换）
     * 
     * @returns Promise<TemplateTestResponse> 渲染结果
     *   {
     *     title: string,
     *     content: string,
     *     emailSubject: string,
     *     emailContent: string
     *   }
     * 
     * 代码示例：
     * ```typescript
     * const result = await notificationApi.testTemplate(1, {
     *   order_no: 'SO20250122001',
     *   amount: '1580.00'
     * })
     * console.log(result.title)   // "您有新订单：SO20250122001"
     * console.log(result.content) // "订单号：SO20250122001，金额：1580.00"
     * ```
     */
    testTemplate: (id: number, testData?: Record<string, any>) => 
      client.post<TemplateTestResponse>(API_ROUTES.notificationAdmin.test(id), { data: testData }),

    // ===== 管理端 API - 事件日志 =====

    /**
     * 获取事件日志列表（分页）
     * 
     * 使用场景：
     * - 查看消息推送历史
     * - 排查发送失败问题
     * 
     * @param params - 查询参数
     *   - notification_id?: number - 消息ID筛选
     *   - user_id?: number - 用户ID筛选
     *   - channel?: string - 渠道筛选
     *   - status?: string - 状态筛选
     *   - start_time?: string - 开始时间
     *   - end_time?: string - 结束时间
     *   - page?: number - 页码
     *   - per_page?: number - 每页数量
     * 
     * @returns Promise<PaginationResult<any>> 分页的事件日志列表
     */
    getEventLogs: (params?: NotificationChannelQueryParams) => 
      client.get<PaginationResult<any>>(API_ROUTES.notificationAdmin.adminNotificationEventLogs, { params }),

    /**
     * 获取事件统计信息
     * 
     * 使用场景：
     * - 分析消息发送情况
     * - 统计各渠道成功率
     * 
     * @param params - 查询参数
     *   - startDate?: string - 开始日期
     *   - endDate?: string - 结束日期
     * 
     * @returns Promise<EventStatisticsResponse> 事件统计
     *   {
     *     total: number,
     *     byEventType: { ... },
     *     byStatus: { ... }
     *   }
     */
    getEventStatistics: (params?: { startDate?: string; endDate?: string }) => 
      client.get<EventStatisticsResponse>(API_ROUTES.notificationAdmin.statistics, { params }),

    // ===== 推送设备管理 =====

    /**
     * 注册推送设备（支持多设备）
     * 
     * 使用场景：
     * - App 启动时或登录后注册设备
     * - 同一用户可在多个设备上登录
     * 
     * @param data - 设备信息
     *   - cid: string - UniPush 客户端ID
     *   - device_id: string - 设备唯一标识
     *   - platform: 'android' | 'ios' | 'harmonyos' - 平台类型
     *   - device_name?: string - 设备名称
     *   - device_model?: string - 设备型号
     *   - os_version?: string - 系统版本
     *   - app_version?: string - App版本
     * 
     * @returns Promise<RegisterDeviceResponse>
     */
    registerPushDevice: (data: RegisterPushDeviceForm) => 
      client.post<RegisterDeviceResponse>(API_ROUTES.push.registerDevice, data),

    /**
     * 移除推送设备
     * 
     * 使用场景：
     * - 用户退出登录时调用
     * - 清除当前设备的推送标识
     * 
     * @param data - 设备标识
     *   - device_id: string - 设备唯一标识
     * 
     * @returns Promise<void>
     */
    removePushDevice: (data: { device_id: string }) => 
      client.delete<void>(API_ROUTES.push.removeDevice, { data }),

    /**
     * 获取用户的推送设备列表
     * 
     * 使用场景：
     * - 在设置页面显示已登录的设备
     * - 管理多设备推送
     * 
     * @returns Promise<PushDevice[]>
     */
    getPushDevices: () => 
      client.get<PushDevice[]>(API_ROUTES.push.listDevices),

    // ===== 系统级通知配置（管理员） =====

    /**
     * 获取系统级通知配置
     * 
     * 使用场景：
     * - 管理员查看系统级通知配置
     * - 包含全局开关、渠道开关、限流、免打扰等配置
     * 
     * @returns Promise<SystemNotificationConfig>
     */
    getSystemConfig: () => 
      client.get<SystemNotificationConfig>(API_ROUTES.notificationSystemConfig.get),

    /**
     * 更新系统全局开关（管理员）
     */
    updateSystemMasterSwitch: (enabled: boolean) => 
      client.put<{ message: string }>(API_ROUTES.notificationSystemConfig.updateMasterSwitch, { enabled }),

    /**
     * 更新渠道开关
     */
    updateChannelSwitch: (channel: string, enabled: boolean) => 
      client.put<{ message: string }>(API_ROUTES.notificationSystemConfig.updateChannelSwitch, { channel, enabled }),

    /**
     * 更新限流配置
     */
    updateRateLimitConfig: (config: RateLimitConfig) => 
      client.put<{ message: string }>(API_ROUTES.notificationSystemConfig.updateRateLimit, config),

    /**
     * 更新免打扰时段配置
     */
    updateQuietHoursConfig: (config: QuietHoursConfig) => 
      client.put<{ message: string }>(API_ROUTES.notificationSystemConfig.updateQuietHours, config),

    /**
     * 清除系统配置缓存
     */
    clearSystemConfigCache: () => 
      client.post<{ message: string }>(API_ROUTES.notificationSystemConfig.clearCache),

    // ===== 系统级通知规则（管理员） =====

    /**
     * 获取所有模块及其通知规则
     */
    getSystemRules: () => 
      client.get<ModuleWithRules[]>(API_ROUTES.notificationSystemRule.list),

    /**
     * 获取指定模块的通知规则
     */
    getModuleRules: (module: string) => 
      client.get<SystemRule[]>(API_ROUTES.notificationSystemRule.module(module)),

    /**
     * 获取单个规则详情
     */
    getSystemRule: (id: number) => 
      client.get<SystemRule>(API_ROUTES.notificationSystemRule.detail(id)),

    /**
     * 创建通知规则
     */
    createSystemRule: (data: CreateSystemRuleForm) => 
      client.post<SystemRule>(API_ROUTES.notificationSystemRule.create, data),

    /**
     * 更新通知规则
     */
    updateSystemRule: (id: number, data: UpdateSystemRuleForm) => 
      client.put<SystemRule>(API_ROUTES.notificationSystemRule.update(id), data),

    /**
     * 删除通知规则
     */
    deleteSystemRule: (id: number) => 
      client.delete<{ message: string }>(API_ROUTES.notificationSystemRule.delete(id)),

    /**
     * 批量更新规则启用状态
     */
    batchUpdateRuleStatus: (ruleIds: number[], isEnabled: boolean) => 
      client.post<{ message: string; count: number }>(API_ROUTES.notificationSystemRule.batchStatus, {
        rule_ids: ruleIds,
        is_enabled: isEnabled,
      }),

    /**
     * 初始化模块默认规则
     */
    initModuleDefaultRules: (module: string) => 
      client.post<{ message: string }>(API_ROUTES.notificationSystemRule.initModule, { module }),

    // ===== 消息监控 API（管理员） =====

    /**
     * 获取监控面板统计数据
     * 
     * 使用场景：
     * - 在监控面板显示今日统计、渠道分布、趋势、最近失败等
     * 
     * @returns Promise<NotificationMonitorDashboard> 监控面板数据
     */
    getMonitorDashboard: () => 
      client.get<NotificationMonitorDashboard>(API_ROUTES.notificationMonitor.dashboard),

    /**
     * 获取发送记录列表
     * 
     * 使用场景：
     * - 在发送记录页面显示所有消息的发送情况
     * - 支持按类型、级别、日期、关键词筛选
     * 
     * @param params - 查询参数
     * @returns Promise<SendRecordsResponse> 发送记录列表
     */
    getSendRecords: (params?: SendRecordsQueryParams) => 
      client.get<SendRecordsResponse>(API_ROUTES.notificationMonitor.sendRecords, { params }),

    /**
     * 获取单条消息的发送详情
     * 
     * 使用场景：
     * - 点击发送记录查看该消息的各渠道发送状态
     * - 包含每个用户、每个渠道的发送结果
     * 
     * @param id - 消息ID
     * @returns Promise<SendRecordDetail> 发送详情
     */
    getSendRecordDetail: (id: number) => 
      client.get<SendRecordDetail>(API_ROUTES.notificationMonitor.sendRecordDetail(id)),

    /**
     * 获取失败记录列表
     * 
     * 使用场景：
     * - 在失败监控页面显示所有发送失败的记录
     * - 支持按渠道、日期、是否可重试筛选
     * 
     * @param params - 查询参数
     * @returns Promise<FailuresResponse> 失败记录列表
     */
    getFailures: (params?: FailuresQueryParams) => 
      client.get<FailuresResponse>(API_ROUTES.notificationMonitor.failures, { params }),

    /**
     * 重试发送失败的单条记录
     * 
     * 使用场景：
     * - 点击失败记录的重试按钮
     * - 只有 retryCount < 3 且状态为 failed 的记录才能重试
     * 
     * @param id - 渠道记录ID
     * @returns Promise<RetryResponse> 重试结果
     */
    retrySend: (id: number) => 
      client.post<RetryResponse>(API_ROUTES.notificationMonitor.retry(id)),

    /**
     * 批量重试失败记录
     * 
     * 使用场景：
     * - 选中多条失败记录后点击批量重试
     * - 或点击"全部重试"按钮
     * 
     * @param data - 包含要重试的记录ID列表
     * @returns Promise<BatchRetryResponse> 批量重试结果
     */
    batchRetry: (data: BatchRetryParams) => 
      client.post<BatchRetryResponse>(API_ROUTES.notificationMonitor.batchRetry, data),
  }
}

// ============================================================================
// 类型导出
// ============================================================================

/** Notification API 类型 */
export type NotificationApi = ReturnType<typeof createNotificationApi>
